import { CheckBox, fromTheme, RadioButton, styled, Text, View } from 'lib_ui-primitives';
import { check } from 'lib_ui-primitives/src/components/customIcons2';
import {
    resolveDisplayModifierColor,
    resolvePillBoxBorderColor
} from 'lib_ui-primitives/src/styling/themes/gl2025/displayModifierColors';
import sizes from 'lib_ui-primitives/src/styling/themes/gl2025/sizes';
import tableStyle, {
    END_OF_RECORDS_TEXT_MARGIN,
    getCheckboxColors,
    INPUT_SIZES,
    ROW_DIMENSIONS
} from 'lib_ui-primitives/src/styling/themes/gl2025/tables';
import lodash from 'lodash';
import { createElement as rc, useEffect, useMemo } from 'react';
import getNestedPatchDetailColumnHNodes from '../columns/PatchDetail/getNestedPatchDetailColumnHNodes';
import useColumnCell from '../columns/useColumnCell';
import {
    _private,
    calculateVisibleColumns,
    createOnClickHandler,
    getFlagStyle,
    getItemState,
    getPillBoxStyle,
    getRowHighlightStyle,
    useRowDetailLogic
} from './useRowDetail.shared';
const { get } = lodash;
//import useWhatChanged from '../../../../utility/useWhatChanged';

const LastRow = styled(View)`
    display: flex;
    flex-direction: row;
    background-color: transparent;
    height: ${fromTheme('listLineHeight')};
    align-items: center;
    cursor: default;
`;
const EndOfRecords = styled(Text).attrs({ name: 'end-of-records' })`
    margin-left: ${fromTheme('textMargin')};
    font-style: italic;
    font-size: ${fromTheme('fontSizeSmall')};
    color: ${fromTheme('disabledFontColor')};
    ${({ theme }) => (theme.selectedTheme === 'gl2025' ? `margin-left: ${END_OF_RECORDS_TEXT_MARGIN}px;` : '')}
`;
EndOfRecords.displayName = 'EndOfRecords';

const OfflineText = styled(Text).attrs({ name: 'offline-text' })`
    margin-left: ${fromTheme('textMargin')};
    font-style: italic;
    font-size: ${fromTheme('fontSizeSmall')};
    color: ${fromTheme('colorScheme', 'error')};
    ${({ theme }) => (theme.selectedTheme === 'gl2025' ? `margin-left: ${END_OF_RECORDS_TEXT_MARGIN}px;` : '')}
`;
OfflineText.displayName = 'OfflineText';

// GL2025 row container with hover styles
// Must match header width behavior: minWidth 100%, but expand to fit-content
// maxWidth + overflow hidden prevents overflow beyond container in single-column or variable column layouts
// Using View instead of 'div' for React Native compatibility
let Gl2025Row = styled(View)`
    display: flex;
    min-width: 100%;
    width: fit-content;
    max-width: 100%;
    overflow: hidden;
    flex-shrink: 0;
`;

const Row = styled(View)`
    display: flex;
    flex-direction: row;
    background-color: ${props =>
        props.singleRowSelect && props.selected
            ? props.theme.listSelectedBackgroundColor
            : props.altRow
              ? 'transparent'
              : props.theme.backgroundColor};
    height: ${fromTheme('listLineHeight')};
    align-items: center;
`;

const FirstCell = styled(View)`
    margin-left: ${fromTheme('viewMargin')};
    margin-right: -${({ theme }) => theme.viewMargin}px;
`;

const CheckBoxFirstCell = styled(View)`
    align-items: center;
    padding: 0 ${fromTheme('viewPaddingMore')} 0 ${fromTheme('viewPadding')};
`;

// Add breaks between cells by increasing border-left-width:
// border-left-width: 1px;
// border-left-color: black;
let NotFirstCell = styled(View)`
    background-color: ${({ altCol, theme }) => (altCol ? theme.backgroundColorLighter : 'transparent')};
    height: ${fromTheme('listLineHeight')};
    align-items: center;
`;

// Re-export _private for testing
export { _private };
export default function useRowDetail(hNode, _columnHNodes, onRowClick, endOfRecordsText, offlineText, PatchDetailTableComponent) {
    // Use shared business logic
    const {
        horizontalScroll,
        activeRowId,
        setActiveRowId,
        visibleColumnIndexes,
        prefixColumnBufferWidth,
        suffixColumnBufferWidth,
        columnHNodes,
        columnWidths,
        theme,
        allowCheckUnknownTags,
        includeCheckbox,
        singleRowSelect,
        relation,
        onIndividualChange,
        isSelected,
        isSelectionDisabled,
        onScrollLayoutChange
    } = useRowDetailLogic(hNode, _columnHNodes, onRowClick, endOfRecordsText, PatchDetailTableComponent);

    // ** Useful for debugging: for figuring out why things are rerendering **/
    //useWhatChanged({ allowCheckUnknownTags, columnHNodes, includeCheckbox, isSelected, onIndividualChange, prefixColumnBufferWidth, suffixColumnBufferWidth, visibleColumnIndexes });
    const RowDetail = useMemo(
        () => props => {
            const {
                item,
                rowIndex,
                isLastItem,
                isOfflineItem,
                isPinned,
                visibleColumnIndexes: overrideVisibleIndexes,
                columnHNodesOverride,
                columnWidthsOverride
            } = props;

            // Use override columns/widths for scrollable grid, or fall back to full set
            const activeColumnHNodes = columnHNodesOverride || columnHNodes;
            const activeColumnWidths = columnWidthsOverride || columnWidths;

            // Calculate visible indices and buffer widths using shared logic
            const { calculatedVisibleIndexes, activePrefixWidth, activeSuffixWidth } = calculateVisibleColumns(
                columnHNodesOverride,
                overrideVisibleIndexes,
                visibleColumnIndexes,
                activeColumnWidths,
                horizontalScroll,
                prefixColumnBufferWidth,
                suffixColumnBufferWidth
            );

            // Get item state using shared logic
            const { _id, isUnknown, disabledCheckbox: itemDisabled, selected } = getItemState(
                item,
                allowCheckUnknownTags,
                isSelected
            );
            const disabledCheckbox = itemDisabled || isSelectionDisabled(_id);
            // This is actually a component that will be rendered so useEffect is ok.
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
                if (isUnknown && !allowCheckUnknownTags && selected) {
                    onIndividualChange(_id, false);
                }
            }, [isUnknown, _id, selected]);

            // Create onClick handler using shared logic
            const onClick = createOnClickHandler(
                _id,
                disabledCheckbox,
                selected,
                onIndividualChange,
                onRowClick,
                singleRowSelect
            );
            /* This part is confusing.  The way we delegate rendering so that the top component
            (e.g. TableList in this case ) has more control, is helpful in some ways and
            confusing in others. If you look at List/index.js, you will see that I increase
            the item count by one if endOfRecordsText is present.  This should allow all
            records to be rendered here.
            */
            if (isOfflineItem) {
                // For narrow pinned column, show offline text directly below End of Records
                // Scrollable column renders empty row for height sync
                if (isPinned === false) {
                    return rc(LastRow, null, '');
                }
                return rc(LastRow, null, rc(OfflineText, null, offlineText));
            }
            if (isLastItem && endOfRecordsText?.length > 0) {
                // prettier-ignore
                // For two-grid layout, only show text in pinned grid (isPinned !== false)
                // Scrollable grid renders empty row for height sync
                return rc(LastRow, null,
                    rc(EndOfRecords, null, isPinned !== false ? endOfRecordsText : '')
                );
            }
            const style = getFlagStyle(item, columnHNodes);

            // Check if gl2025 theme is active
            const isGl2025 = theme.selectedTheme === 'gl2025';

            if (isGl2025) {
                const darkMode = theme.darkMode;
                const styles = tableStyle[darkMode ? 'dark' : 'light'].row;
                // rowIndex starts at 1 (index 0 is header), so first data row is odd
                // Use (rowIndex - 1) to get 0-based visual index for alternation
                const rowType = (rowIndex - 1) % 2 === 0 ? 'primary' : 'secondary';
                const rowStyles = styles.default[rowType];

                // Use calculatedVisibleIndexes which accounts for column overrides
                const actualVisibleIndexes = calculatedVisibleIndexes;
                // For two-grid: only render checkbox in pinned grid (or if isPinned not specified for backwards compat)
                const showCheckbox = isPinned !== false && includeCheckbox;

                // Display modifier: row highlight (color-coded row background from foreign relation)
                const rowHighlightStyle = getRowHighlightStyle(item, columnHNodes, resolveDisplayModifierColor, darkMode, resolvePillBoxBorderColor);

                // Determine row state styling
                // 'selected' is when checkbox checked (not highlighted)
                // 'isActiveRow' is when row was clicked (highlighted)
                const isActiveRow = _id === activeRowId;
                let containerStyle = { ...rowStyles.container, ...style, ...rowHighlightStyle };
                let textStyle = rowHighlightStyle.color
                    ? { ...rowStyles.text, color: rowHighlightStyle.color }
                    : rowStyles.text;

                if (isActiveRow) {
                    containerStyle = { ...containerStyle, ...styles.selected.container };
                    textStyle = { ...textStyle, ...styles.selected.text };
                }

                const isChecked = (!isUnknown || allowCheckUnknownTags) && selected;
                const checkboxColors = getCheckboxColors(darkMode, isChecked);
                const checkboxStyle = {
                    cursor: 'pointer',
                    width: `${INPUT_SIZES.checkbox.width}px`,
                    height: `${INPUT_SIZES.checkbox.height}px`,
                    margin: 0,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    border: `${INPUT_SIZES.checkbox.borderWidth}px solid`,
                    borderColor: checkboxColors.borderColor,
                    borderRadius: `${INPUT_SIZES.checkbox.borderRadius}px`,
                    backgroundColor: checkboxColors.backgroundColor,
                    backgroundImage: isChecked
                        ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='${check}'/%3E%3C/svg%3E")`
                        : 'none',
                    backgroundSize: `${INPUT_SIZES.checkbox.iconSize}px ${INPUT_SIZES.checkbox.iconSize}px`,
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    position: 'relative',
                    flexShrink: 0
                };

                // Checkbox cell style. Must match header checkbox layout
                const checkboxCellStyle = {
                    width: `${sizes.fixedSpacing['6xl']}px`,
                    minWidth: `${sizes.fixedSpacing['6xl']}px`,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: `${sizes.fixedSpacing.md}px`,
                    flexShrink: 0
                    // backgroundColor removed to inherit hover color from parent Gl2025Row
                };

                const cellStyle = {
                    padding: `${ROW_DIMENSIONS.padding.vertical}px ${ROW_DIMENSIONS.padding.horizontal}px`,
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                    flexShrink: 0
                };

                const handleRowClick = () => {
                    setActiveRowId(_id);
                    onRowClick?.({ _id });
                };

                const handleCheckboxChange = e => {
                    e.stopPropagation();
                    setActiveRowId(_id);
                    onClick(e.target.checked);
                };

                // prettier-ignore
                return rc(Gl2025Row, { style: { ...containerStyle, cursor: 'pointer' }, onClick: handleRowClick, key: `row-${_id}` },  
                    // Checkbox cell (only in pinned grid for two-grid layout)
                    showCheckbox && rc(View, { style: checkboxCellStyle, key: 'checkbox-cell', onClick: e => e.stopPropagation() },
                        !singleRowSelect && rc('input', {  
                            type: 'checkbox',
                            checked: (!isUnknown || allowCheckUnknownTags) && selected,
                            disabled: disabledCheckbox,
                            onChange: handleCheckboxChange,
                            className: 'gl2025-row-checkbox',
                            style: checkboxStyle
                        }),
                        singleRowSelect && rc('input', {  
                            type: 'radio',
                            name: relation,
                            checked: (!isUnknown || allowCheckUnknownTags) && selected,
                            disabled: disabledCheckbox,
                            onChange: e => { e.stopPropagation(); setActiveRowId(_id); onClick(true); },
                            style: {
                                ...checkboxStyle,
                                borderRadius: '50%',
                                backgroundImage: isChecked
                                    ? 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3E%3Ccircle cx=\'8\' cy=\'8\' r=\'4\' fill=\'white\'/%3E%3C/svg%3E")'
                                    : 'none'
                            }
                        })
                    ),
                    // Prefix buffer for column virtualization (only for scrollable grid)
                    !isPinned && rc(View, { key: 'prefixColumn', style: { width: activePrefixWidth, flexShrink: 0 } }),
                    // Data cells (use actualVisibleIndexes for two-grid support)
                    activeColumnHNodes.flatMap((colHNode, i) => {
                        if (!actualVisibleIndexes.includes(i)) {
                            return null;
                        }
                        
                        let columns = [colHNode];
                        const width = activeColumnWidths[i];
                        let cellItem = item;
                        
                        if (colHNode.hNodeType === 'PatchDetail') {
                            columns = getNestedPatchDetailColumnHNodes();
                            const patchValue = get(item, colHNode.propertyName);
                            cellItem = patchValue && patchValue[0] ? patchValue[0] : {};
                        }
                        
                        return columns.map((c, j) => {
                            const cellHNode = colHNode.hNodeType === 'PatchDetail' ? c : colHNode;
                            const cellWidth = colHNode.hNodeType === 'PatchDetail' ? c.width : width;

                            const currentTextStyle = colHNode.bold === true ? rowStyles.idText : textStyle;

                            // Display modifier: pill box (colored pill around cell value)
                            const pillStyle = getPillBoxStyle(item, colHNode, resolveDisplayModifierColor, darkMode, resolvePillBoxBorderColor);

                            return rc(View, {
                                // i - column index, j - row index
                                key: `cell-${i}-${j}`,
                                style: {
                                    ...cellStyle,
                                    ...currentTextStyle,
                                    minWidth: `${cellWidth}px`,
                                    width: `${cellWidth}px`
                                }
                            },
                                // Don't pass width to Cell - outer container handles width for gl2025
                                pillStyle
                                    ? rc(View, { style: pillStyle },
                                        rc(Cell, { item: cellItem, hNode: cellHNode, PatchDetailTableComponent }))
                                    : rc(Cell, { item: cellItem, hNode: cellHNode, PatchDetailTableComponent })
                            );
                        });
                    }),
                    // Suffix buffer for column virtualization (only for scrollable grid)
                    !isPinned && rc(View, { key: 'suffixColumn', style: { width: activeSuffixWidth, flexShrink: 0 } })
                );
            }

            // Default (legacy) row rendering
            // prettier-ignore
            return rc(Row, { altRow: rowIndex % 2 === 0, singleRowSelect, selected, style, role: 'row'},
                // blank view to take up space
                includeCheckbox && rc(CheckBoxFirstCell, {},
                    !singleRowSelect && rc(CheckBox, { disabled: disabledCheckbox, onClick, value: (!isUnknown || allowCheckUnknownTags) && selected }),
                    singleRowSelect && rc(RadioButton, { id:_id, disabled: disabledCheckbox, name:relation, onClick, value: (!isUnknown || allowCheckUnknownTags) && selected })
                ),
                rc(View, { key: 'prefixColumn', name: 'prefix-column', style: { width: prefixColumnBufferWidth } }),
                columnHNodes.flatMap((colHNode, i) => {
                    if (!visibleColumnIndexes.includes(i)) {
                        return null;
                    }
                    
                    let columns = [colHNode];
                    const width = columnWidths[i];
                    let cellItem = item;
                    
                    if (colHNode.hNodeType === 'PatchDetail') {
                        columns = getNestedPatchDetailColumnHNodes();
                        const patchValue = get(item, colHNode.propertyName);
                        cellItem = patchValue && patchValue[0] ? patchValue[0] : {};
                    }
                    
                    return columns.map((c, j) => {
                        const cellHNode = colHNode.hNodeType === 'PatchDetail' ? c : colHNode;
                        const cellWidth = colHNode.hNodeType === 'PatchDetail' ? c.width : width;
                        // i - column index, j - row index
                        return i === 0 && j === 0 && !includeCheckbox
                            ? rc(FirstCell, { key: `cell-${i}-${j}` }, rc(Cell, { item: cellItem, hNode: cellHNode, width: cellWidth, PatchDetailTableComponent }))
                            : rc(NotFirstCell, { altCol: (i + j) % 2 === 1, key: `cell-${i}-${j}` }, rc(Cell, { item: cellItem, hNode: cellHNode, width: cellWidth, PatchDetailTableComponent }));
                    });
                }),
                rc(View, { key: 'suffixColumn', name: 'suffix-column', style: { width: suffixColumnBufferWidth } })
            );
        },
        [
            allowCheckUnknownTags,
            columnHNodes,
            includeCheckbox,
            isSelected,
            isSelectionDisabled,
            onIndividualChange,
            onRowClick,
            prefixColumnBufferWidth,
            relation,
            singleRowSelect,
            suffixColumnBufferWidth,
            visibleColumnIndexes,
            endOfRecordsText,
            offlineText,
            columnWidths,
            PatchDetailTableComponent,
            theme,
            activeRowId,
            setActiveRowId,
            horizontalScroll
        ]
    );

    RowDetail.displayName = 'RowDetail';
    return [RowDetail, onScrollLayoutChange, { columnWidths, columnHNodes }];
}

function Cell(props) {
    const { item, hNode, width, PatchDetailTableComponent } = props;
    // Call useColumnCell directly at the top level of this component function.
    // The useMemo was causing a Rules of Hooks violation.
    // Memoization, if needed, can be applied to the Cell component itself (e.g., React.memo(Cell))
    // or within useColumnCell.
    return useColumnCell(item, hNode, undefined, width, undefined, undefined, undefined, PatchDetailTableComponent);
}

// getFlagStyle is now imported from shared file
